import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { connectParityApi, normalizeUserId } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

/** Someone's Teams-style status message. */
export interface PeerNote {
    message: string;
    emoji?: string | null;
    expiresAt?: string | null;
    showWhenMessaged?: boolean | null;
}

interface PeerStatusValue {
    /** "online" | "away" | "busy" | "dnd" | "brb" | "offline" — as that person chose to be seen. */
    presenceOf: (userId: string | null | undefined) => string;
    /** Their status message, or null when none is set (or it has expired). */
    noteOf: (userId: string | null | undefined) => PeerNote | null;
}

const PeerStatusContext = createContext<PeerStatusValue>({
    presenceOf: () => "offline",
    noteOf: () => null
});

function live(note: PeerNote | null | undefined): PeerNote | null {
    if (!note || !note.message) return null;
    if (note.expiresAt && new Date(note.expiresAt).getTime() <= Date.now()) return null;
    return note;
}

/**
 * Presence and status messages for everyone in my tenant: the /api/presence
 * snapshot (refreshed every 30 s) overlaid with live hub updates from the
 * shell. Chat headers, the chat list and people cards all read from here.
 * `liveNotes` holds a key only once the hub has said something about that
 * person's note — null there means "cleared", which beats the snapshot.
 */
export function PeerStatusProvider({ livePresence, liveNotes, children }: {
    livePresence: Record<string, string>;
    liveNotes: Record<string, PeerNote | null>;
    children: React.ReactNode;
}) {
    const token = useAccessToken();
    const snapshot = useQuery({
        queryKey: ["connect-presence", token],
        queryFn: () => connectParityApi.listPresence(token),
        enabled: Boolean(token),
        refetchInterval: 30_000
    });
    const byId = useMemo(() => {
        const map = new Map<string, { status: string; note: PeerNote | null }>();
        for (const p of snapshot.data ?? []) {
            map.set(normalizeUserId(p.userId), {
                status: p.status,
                note: p.statusMessage
                    ? { message: p.statusMessage, emoji: p.statusEmoji, expiresAt: p.statusExpiresAt, showWhenMessaged: p.showWhenMessaged }
                    : null
            });
        }
        return map;
    }, [snapshot.data]);

    const presenceOf = useCallback((userId: string | null | undefined) => {
        if (!userId) return "offline";
        const id = normalizeUserId(userId);
        return livePresence[id] ?? byId.get(id)?.status ?? "offline";
    }, [livePresence, byId]);

    const noteOf = useCallback((userId: string | null | undefined) => {
        if (!userId) return null;
        const id = normalizeUserId(userId);
        return live(id in liveNotes ? liveNotes[id] : byId.get(id)?.note);
    }, [liveNotes, byId]);

    const value = useMemo(() => ({ presenceOf, noteOf }), [presenceOf, noteOf]);
    return <PeerStatusContext.Provider value={value}>{children}</PeerStatusContext.Provider>;
}

export function usePeerStatus(): PeerStatusValue {
    return useContext(PeerStatusContext);
}

/** "🌴 Out until Monday" — one line, truncated, full text on hover. */
export function StatusNoteText({ note, className }: { note: PeerNote; className?: string }) {
    const text = `${note.emoji ? `${note.emoji} ` : ""}${note.message}`;
    return (
        <span className={cn("truncate", className)} title={text}>
            {text}
        </span>
    );
}

/** Plain-text form for tooltips and aria labels. */
export function noteText(note: PeerNote | null | undefined): string {
    return note ? `${note.emoji ? `${note.emoji} ` : ""}${note.message}` : "";
}
